import { useQuery } from "@tanstack/react-query";
import {
  getAllPlansPricing,
  getPlanPricing,
  type PlanPricingData,
  type PlanPricingOption,
} from "@/lib/supabase-marketing";

export type { PlanPricingData, PlanPricingOption };

/**
 * Hook to fetch plan pricing for both monthly and annual billing intervals.
 * Combines data from carrier_plan_configs and plan_billing_discount_configs.
 * The join lives in src/lib/supabase-marketing.ts; this hook just adds
 * React Query caching.
 */
export function usePlanPricing(planId: string) {
  return useQuery({
    queryKey: ["plan-pricing", planId],
    queryFn: (): Promise<PlanPricingData | null> => getPlanPricing(planId),
    enabled: !!planId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch pricing for multiple plans at once.
 */
export function useMultiplePlansPricing(planIds: string[], enabled = true) {
  return useQuery({
    queryKey: ["multiple-plans-pricing", planIds],
    queryFn: (): Promise<Map<string, PlanPricingData>> => getAllPlansPricing(planIds),
    enabled: enabled && planIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
